import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { userService } from '../../_services/user.service';

function GlicAddApiPopup({ text, closeGlicAddApiPopup, glicIps = [], ipAddressUpdated, stypeChanged }) {
    const [loading, setLoading] = useState(false);
    const [ipAddress, setIpAddress] = useState('');
    const [sType, setSType] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [disableBtn, setDisableBtn] = useState(false);

    const userdata = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user'));
        } catch {
            return null;
        }
    }, []);

    useEffect(() => {
        if (glicIps?.length > 0) {
            const definedStype = glicIps[0].sType === 'Dynamic IP';
            setSType(definedStype);
        }
    }, [glicIps]);

    const handleStypeChange = useCallback((checked) => {
        setErrorMessage('');
        setSuccessMessage('');
        setSType(checked);
        stypeChanged?.(checked);
    }, [stypeChanged]);

    const handleInputChange = useCallback((e) => {
        setErrorMessage('');
        setSuccessMessage('');
        setIpAddress(e.target.value);
    }, []);

    const addWhiteListedIPs = useCallback(async () => {
        const isDynamic = sType;
        const checkExisting = glicIps.find((ip) => ip.ipAddress === ipAddress);
        const ipv4Regex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
        const domainRegex = /^(?:https?:\/\/)?(?:([a-zA-Z0-9-]+)\.)?([a-zA-Z0-9-]+)\.([a-zA-Z]{2,})(?:\/|$)/;

        if (!ipAddress) {
            const errorMsgStr = isDynamic 
                ? 'Domain name is required. Example: example.com or sub.example.com' 
                : 'IP address is required. Example: 192.168.1.1';
            setErrorMessage(errorMsgStr);
            setSuccessMessage('');
            return;
        }

        if (!domainRegex.test(ipAddress) && isDynamic) {
            setErrorMessage('Domain name is not valid. Example: example.com or sub.example.com');
            setSuccessMessage('');
            return;
        }

        if (!ipv4Regex.test(ipAddress) && !isDynamic) {
            setErrorMessage('IP address is not valid. Example: 192.168.1.1');
            setSuccessMessage('');
            return;
        }

        if (checkExisting) {
            setErrorMessage('IP address is already added in the list. Please try another IP.');
            setSuccessMessage('');
            return;
        }

        setDisableBtn(true);
        
        try {
            const apidata = await userService.ProcessWhiteListIP(
                userdata.wholesaler_Id.toString(),
                userdata.email,
                ipAddress,
                sType
            );
            
            if (apidata[0]?.code === '1') {
                setSuccessMessage(apidata[0].message);
                setErrorMessage('');
                ipAddressUpdated?.();
                
                setTimeout(() => {
                    setDisableBtn(false);
                    closeGlicAddApiPopup?.();
                }, 2000);
            } else {
                setErrorMessage(apidata[0]?.message || 'An error occurred');
                setSuccessMessage('');
                setDisableBtn(false);
            }
        } catch (error) {
            setErrorMessage('An error occurred. Please try again.');
            setDisableBtn(false);
        }
    }, [ipAddress, sType, glicIps, userdata, ipAddressUpdated, closeGlicAddApiPopup]);

    const handleOpenChange = useCallback((open) => {
        if (!open) {
            closeGlicAddApiPopup();
        }
    }, [closeGlicAddApiPopup]);

    return (
        <Dialog open={true} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{text}</DialogTitle>
                </DialogHeader>

                <div className="flex flex-col gap-4">
                    {loading ? (
                        <div className="flex items-center justify-center p-8 min-h-[120px]">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        </div>
                    ): 
                    <div className="space-y-4">
                        {/* Dynamic IP Checkbox - only show if no existing IPs */}
                        {!glicIps.length && (
                            <div className="flex items-center space-x-2">
                                <Checkbox
                                    id="sType"
                                    checked={sType}
                                    onCheckedChange={handleStypeChange}
                                />
                                <Label htmlFor="sType" className="cursor-pointer">
                                    Add Dynamic IP
                                </Label>
                            </div>
                        )}

                        {/* IP/Domain Input */}
                        <div className="flex flex-col gap-2">
                            <Label htmlFor="ipAddress">
                                {sType ? 'Domain Name' : 'IP Address'}
                            </Label>
                            <Input
                                id="ipAddress"
                                type="text"
                                value={ipAddress}
                                onChange={handleInputChange}
                                placeholder={`Please enter ${sType ? "Domain name" : "IP address"}`}
                                autoComplete="off"
                            />
                        </div>

                        {/* Error/Success Message */}
                        {(errorMessage || successMessage) && (
                            <p className={`text-sm ${errorMessage ? 'text-destructive' : 'text-green-600'}`}>
                                {errorMessage || successMessage}
                            </p>
                        )}
                    </div>}

                </div>

                <DialogFooter>
                    <Button 
                        onClick={addWhiteListedIPs}
                        disabled={disableBtn}
                    >
                        {disableBtn ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Saving...
                            </>
                        ) : (
                            'Save'
                        )}
                    </Button>
                    <Button variant="secondary" onClick={closeGlicAddApiPopup}>
                        Close
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default GlicAddApiPopup;
